/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { jsx } from '@emotion/react'
import { Fragment } from 'react'

import { EuiHealth, EuiLink, EuiSpacer, EuiTableRowCell, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { CuiTableColumn } from '@modules/cui/Table'
import { CuiHelpTipIcon } from '@modules/cui/HelpTipIcon'
import CuiElasticConsumptionUnits from '@modules/cui/formatters/CuiElasticConsumptionUnits'
import type { CCMInstance } from '@modules/billing-api/customTypes'
import { colorForInstances } from '@modules/billing-lib/colorGenerator'

import BillingUsageTable from '@/components/Billing/BillingUsage/BillingUsageOverviewV2/BillingUsageTable'

import AggregationTableRowV2 from '../components/AggregationTableRowV2'
import { ColorKeyForOtherCCM, VisibleInstances } from '../constants'

import { getRowColor, getTotalCost } from './utils'

import type { TotalCCMCollapsedCosts } from './types'
import type { FunctionComponent } from 'react'

interface Props {
  instances: CCMInstance[]
  isAggregated: boolean
  isLoading: boolean
  onSelect: (id: string, name?: string) => void
  organizationId: string
}

const ConnectedClustersList: FunctionComponent<Props> = ({
  instances,
  isAggregated,
  isLoading,
  onSelect,
  organizationId,
}) => {
  const items = instances.sort((a, b) => b.total_ecu - a.total_ecu)
  const totalCost = getTotalCost(items)
  const visibleItems = items.slice(0, VisibleInstances)
  const collapsedItems = items.slice(VisibleInstances)
  const collapsedTotals = getTotalCost(collapsedItems)

  const columns: Array<CuiTableColumn<CCMInstance>> = [
    {
      label: <FormattedMessage id='billing-usage-ccm-list.name-label' defaultMessage='Name' />,
      render: ({ name, id }) => (
        <EuiHealth
          color={isAggregated ? colorForInstances.get(ColorKeyForOtherCCM) : getRowColor(id)}
        >
          <EuiLink disabled={!id} onClick={() => onSelect(id, name)}>
            <span data-test-id='name'>{name || id}</span>
          </EuiLink>

          {name && (
            <Fragment>
              <EuiSpacer size='xs' />
              <EuiText color='subdued' size='xs'>
                {id}
              </EuiText>
            </Fragment>
          )}
        </EuiHealth>
      ),
      width: '60%',
      footer: {
        render: () => (
          <Fragment>
            <FormattedMessage id='billing-usage-ccm-list.total' defaultMessage='Total usage' />
            <CuiHelpTipIcon iconType='info'>
              <FormattedMessage
                id='billing-deployment-usage.tip-icon'
                defaultMessage='Usage only. Does not include credits, prepaids, or any other discounts.'
              />
            </CuiHelpTipIcon>
          </Fragment>
        ),
      },
    },
    {
      label: (
        <FormattedMessage id='billing-usage-ccm-list.solution-label' defaultMessage='Solution' />
      ),
      render: () => (
        <FormattedMessage id='billing-usage-ccm-list.search-type' defaultMessage='Cloud Connect' />
      ),
      footer: {
        render: () => null,
      },
    },
    {
      align: 'right',
      label: (
        <FormattedMessage id='billing-usage-ccm-list.total-label' defaultMessage='Total (in ECU)' />
      ),
      render: ({ total_ecu }) => (
        <CuiElasticConsumptionUnits value={total_ecu} unit='none' dp={4} withSymbol={false} />
      ),
      footer: {
        render: () => (
          <span data-test-id='ccm-total'>
            <CuiElasticConsumptionUnits
              value={totalCost}
              unit='none'
              dp={4}
              withSymbol={false}
              symbolPosition='before'
            />
          </span>
        ),
      },
    },
    {
      actions: true, // additional actions column
      width: '40px',
    },
  ]

  return (
    <BillingUsageTable<CCMInstance>
      title={
        <FormattedMessage id='billing-ccm-usage-list.title' defaultMessage='Connected clusters' />
      }
      rows={visibleItems}
      columns={columns}
      isLoading={isLoading}
      organizationId={organizationId}
      dataTestId='ccm-list-usage-table'
      renderCustomRowsLast={() => (
        <AggregationTableRowV2<TotalCCMCollapsedCosts, CCMInstance>
          onItemSelected={onSelect}
          totals={{ total_ecu: collapsedTotals }}
          items={collapsedItems}
          type='cloud_connect'
          renderRow={({ total_ecu }) => (
            <Fragment>
              <EuiTableRowCell>
                <FormattedMessage
                  id='billing-usage-ccm-list.search-type'
                  defaultMessage='Cloud Connect'
                />
              </EuiTableRowCell>
              <EuiTableRowCell align='right'>
                <CuiElasticConsumptionUnits
                  value={total_ecu}
                  unit='none'
                  dp={4}
                  withSymbol={false}
                />
              </EuiTableRowCell>
              <EuiTableRowCell className='euiTableCellContent' />
            </Fragment>
          )}
        />
      )}
    />
  )
}

export default ConnectedClustersList
